import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { AlertCircle, Check, ArrowLeft } from 'lucide-react';
import {
  useSignInMutation,
  useSignUpMutation,
  useRequestPasswordResetMutation,
} from './api';
import { BrandMark } from '../onboarding/BrandMark';
import { HowItWorksDialog } from '../onboarding/HowItWorks';
import { track } from '../onboarding/track';

export function AuthScreen({
  intro,
  initialMode = 'signin',
}: {
  /** Shown above the form — e.g. who an invite link leads to. */
  intro?: ReactNode;
  initialMode?: 'signin' | 'signup';
} = {}) {
  const [mode, setMode] = useState<'signin' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [howOpen, setHowOpen] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const [signIn, { isLoading: signingIn }] = useSignInMutation();
  const [signUp, { isLoading: signingUp }] = useSignUpMutation();
  const [requestReset, { isLoading: requestingReset }] = useRequestPasswordResetMutation();
  const busy = signingIn || signingUp || requestingReset;

  // The top of the sign-up funnel: the create-account form came into view.
  useEffect(() => {
    if (mode === 'signup') track({ event: 'signup_started' });
  }, [mode]);

  const switchMode = useCallback((next: 'signin' | 'signup') => {
    setMode(next);
    setForgot(false);
    setResetSent(false);
    setError(null);
  }, []);

  const onSubmit = useCallback(async () => {
    setError(null);
    try {
      if (mode === 'signin') {
        await signIn({ email, password }).unwrap();
      } else {
        await signUp({ email, password, name }).unwrap();
        track({ event: 'account_created' });
        // Sign-up makes the account but no session (autoSignIn is off, and the
        // guarded sign-up route answers without a cookie), so sign straight in
        // — as the mail invite does — rather than leaving them on sign-in.
        await signIn({ email, password }).unwrap();
      }
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      if (Array.isArray(err.data?.message)) {
        setError(err.data!.message[0]!);
      } else {
        setError(err.data?.message ?? (err.status === 'FETCH_ERROR' ? 'You appear to be offline' : 'Could not sign in'));
      }
    }
  }, [mode, email, password, name, signIn, signUp]);

  const onForgotSubmit = useCallback(async () => {
    setError(null);
    try {
      // Same-origin reset page; Better Auth checks this against trustedOrigins.
      const redirectTo = `${window.location.origin}/#/reset-password`;
      await requestReset({ email, redirectTo }).unwrap();
      setResetSent(true);
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      const message = Array.isArray(err.data?.message) ? err.data!.message[0] : err.data?.message;
      setError(message ?? 'Could not send the reset link');
    }
  }, [email, requestReset]);

  return (
    <main className="auth-page">
      <BrandMark />

      <section className="auth-pitch" aria-label="About toiyp">
        {/* An invite note already says what toiyp is; keep the form in view. */}
        {!intro && (
          <>
            <p className="auth-pitch__line">
              Don&apos;t text me, <span className="brand-wordmark brand-wordmark--inline">toiyp</span> me.
            </p>
            <p className="auth-pitch__body">
              Letters for the people you actually know: old friends, family, someone you just met.
              They arrive slowly, like real post, so you both write properly.
            </p>
          </>
        )}
        <button type="button" className="linkish auth-pitch__how" onClick={() => setHowOpen(true)}>
          How does it work?
        </button>
      </section>
      {howOpen && <HowItWorksDialog onClose={() => setHowOpen(false)} />}

      <div className="auth-card">
        {forgot ? (
          resetSent ? (
            <div className="auth-form" style={{ textAlign: 'center', paddingTop: 8 }}>
              <p className="form-success" role="status">
                <Check size={14} aria-hidden="true" />
                If that address is on toiyp, a reset link is on its way.
              </p>
              <p className="field__hint" style={{ margin: '12px 0 20px' }}>
                The link expires in about an hour. Check the spam folder if it
                takes a while.
              </p>
              <button
                type="button"
                className="form-submit"
                onClick={() => {
                  setResetSent(false);
                  setForgot(false);
                }}
              >
                Back to sign in
              </button>
            </div>
          ) : (
            <form
              className="auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                void onForgotSubmit();
              }}
            >
              <button
                type="button"
                className="linkish auth-back"
                onClick={() => {
                  setForgot(false);
                  setError(null);
                }}
              >
                <ArrowLeft size={14} aria-hidden="true" /> Back
              </button>
              <p className="auth-intro">
                Enter the address you signed up with and we&apos;ll send a reset
                link.
              </p>

              <div className="field">
                <label className="field__label" htmlFor="auth-forgot-email">Email</label>
                <input
                  id="auth-forgot-email"
                  className="field__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>

              {error && (
                <p className="form-error" role="alert">
                  <AlertCircle size={14} aria-hidden="true" />
                  {error}
                </p>
              )}

              <button type="submit" className="form-submit" disabled={busy}>
                {requestingReset ? '…' : 'Send reset link'}
              </button>
            </form>
          )
        ) : (
          <>
            {intro}
            <div className="auth-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signin'}
                className={`auth-tab${mode === 'signin' ? ' active' : ''}`}
                onClick={() => switchMode('signin')}
              >
                Sign in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                className={`auth-tab${mode === 'signup' ? ' active' : ''}`}
                onClick={() => switchMode('signup')}
              >
                Create account
              </button>
            </div>

            <form
              className="auth-form"
              onSubmit={(e) => {
                e.preventDefault();
                void onSubmit();
              }}
            >
              {mode === 'signup' && (
                <div className="field">
                  <label className="field__label" htmlFor="auth-name">Name</label>
                  <input
                    id="auth-name"
                    className="field__input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="How you sign letters"
                    autoComplete="name"
                  />
                </div>
              )}

              <div className="field">
                <label className="field__label" htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  className="field__input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="auth-password">Password</label>
                <input
                  id="auth-password"
                  className="field__input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="at least 8 characters"
                  required
                  minLength={8}
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                />
              </div>

              {error && (
                <p className="form-error" role="alert">
                  <AlertCircle size={14} aria-hidden="true" />
                  {error}
                </p>
              )}

              <button type="submit" className="form-submit" disabled={busy}>
                {busy ? '…' : mode === 'signin' ? 'Sign in' : 'Create account'}
              </button>

              {mode === 'signin' && (
                <button
                  type="button"
                  className="linkish auth-forgot"
                  onClick={() => {
                    setForgot(true);
                    setError(null);
                  }}
                >
                  Forgot your password?
                </button>
              )}
            </form>
          </>
        )}
      </div>
    </main>
  );
}